// pages/emergency/Emergency.jsx 
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Phone, MapPin, Ambulance as AmbulanceIcon, 
  Clock, AlertCircle, Filter, 
  X, Star, CheckCircle, Info, Navigation
} from 'lucide-react';
import ambulanceService from '../../services/ambulanceService';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Emergency = () => {
  const navigate = useNavigate();
  
  // Location state
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);

  // Ambulances state
  const [ambulances, setAmbulances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    radius: 50,
    serviceType: '',
    sortBy: 'distance'
  });
  const [showFilters, setShowFilters] = useState(false);

  const serviceTypes = [
    { value: '', label: 'All Services' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'non-emergency', label: 'Non-Emergency' },
    { value: 'patient_transfer', label: 'Patient Transfer' },
    { value: 'event_standby', label: 'Event Standby' },
    { value: 'medical_transport', label: 'Medical Transport' }
  ];

  // Initial load - Don't auto-request location
  useEffect(() => {
    const initLoad = async () => {
      setInitialLoadComplete(true);
      // Load ambulances without location first
      await fetchAmbulancesWithoutLocation();
    };
    initLoad();
  }, []);

  // Fetch ambulances when location or filters change
  useEffect(() => {
    if (initialLoadComplete) {
      fetchAmbulances();
    }
  }, [currentLocation, filters, initialLoadComplete]);

  // Fetch ambulances without location (for initial load)
  const fetchAmbulancesWithoutLocation = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        limit: 50,
        isActive: true,
        isVerified: true
      };

      if (filters.serviceType) {
        params.serviceType = filters.serviceType;
      }

      const response = await ambulanceService.getAll(params);

      if (response.success) {
        let ambulancesList = response.data?.ambulances || [];

        // Sort by rating if no location
        if (filters.sortBy === 'rating') {
          ambulancesList.sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0));
        } else if (filters.sortBy === 'price') {
          ambulancesList.sort((a, b) => (a.pricing?.baseFee || 0) - (b.pricing?.baseFee || 0));
        }

        setAmbulances(ambulancesList);
      } else {
        setError(response.message || 'Failed to fetch ambulances');
      }
    } catch (err) {
      console.error('Fetch ambulances error:', err);
      setError('Failed to fetch ambulances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Request location (user-initiated)
  const getCurrentLocation = async () => {
    setIsGettingLocation(true);
    setLocationError('');
    setError('');
    setShowLocationPrompt(false);
    
    const result = await ambulanceService.getCurrentLocation();
    
    if (result.success) {
      setCurrentLocation(result.data);
      setLocationError('');
      
      // Update to distance sort if location is available
      if (filters.sortBy === 'rating' || filters.sortBy === 'price') {
        setFilters(prev => ({ ...prev, sortBy: 'distance' }));
      }
    } else {
      setLocationError(result.message);
      setError(`Unable to get your location: ${result.message}`);
      
      if (result.suggestions?.length > 0) {
        setError(prev => prev + '\n\nSuggestions:\n' + result.suggestions.join('\n'));
      }
    }
    
    setIsGettingLocation(false);
  };

  // Fetch ambulances with filters
  const fetchAmbulances = async () => {
    try {
      setLoading(true);
      setError('');

      let response;

      if (currentLocation) {
        // Fetch nearby ambulances if location is available
        const params = {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          radius: filters.radius,
          limit: 50
        };

        if (filters.serviceType) {
          params.serviceType = filters.serviceType;
        }

        response = await ambulanceService.getNearby(params);
      } else {
        // Fetch all ambulances if location is not available
        const params = {
          limit: 50,
          isActive: true,
          isVerified: true
        };

        if (filters.serviceType) {
          params.serviceType = filters.serviceType;
        }

        response = await ambulanceService.getAll(params);
      }

      if (response.success) {
        let ambulancesList = response.data?.ambulances || [];

        // Sort ambulances
        if (currentLocation && filters.sortBy === 'distance') {
          ambulancesList.sort((a, b) => (a.distance || 0) - (b.distance || 0));
        } else if (filters.sortBy === 'rating') {
          ambulancesList.sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0));
        } else if (filters.sortBy === 'price') {
          ambulancesList.sort((a, b) => (a.pricing?.baseFee || 0) - (b.pricing?.baseFee || 0));
        }

        setAmbulances(ambulancesList);
      } else {
        setError(response.message || 'Failed to fetch ambulances');
      }
    } catch (err) {
      console.error('Fetch ambulances error:', err);
      setError('Failed to fetch ambulances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDistance = (distance) => {
    if (!distance) return 'N/A';
    return distance < 1 
      ? `${(distance * 1000).toFixed(0)}m` 
      : `${distance.toFixed(1)}km`;
  };

  const getEstimatedTime = (distance) => {
    if (!distance) return 'N/A';
    const timeInHours = distance / 50;
    const timeInMinutes = Math.ceil(timeInHours * 60);
    return `${timeInMinutes} mins`;
  };

  const handleRequestAmbulance = (ambulance) => {
    navigate('/ambulances/request', { 
      state: { 
        ambulance,
        pickupLocation: currentLocation
      } 
    });
  };

  const handleViewDetails = (ambulanceId) => {
    navigate(`/ambulances/${ambulanceId}`);
  };

  const handleCallDirect = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
      {/* Emergency Header */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center">
                <AlertTriangle className="w-10 h-10 mr-3 animate-pulse" />
                Emergency Ambulance Services
              </h1>
              <p className="text-red-100">Find and request ambulances - 24/7 Support Available</p>
            </div>
            
            <a 
              href="tel:911"
              className="bg-white text-red-600 px-6 py-3 rounded-lg hover:bg-red-50 transition font-semibold flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call 911
            </a>
          </div>
        </div>
      </section>

      {/* Location Permission Prompt Banner */}
      {showLocationPrompt && !currentLocation && !isGettingLocation && (
        <section className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1">
                <div className="bg-blue-500 text-white p-2 rounded-full">
                  <MapPin size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-blue-900 font-medium">Find nearest ambulances</p>
                  <p className="text-blue-700 text-sm">
                    Allow location access to see ambulances near you with accurate ETAs
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={getCurrentLocation}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  Enable Location
                </button>
                <button
                  onClick={() => setShowLocationPrompt(false)}
                  className="text-blue-600 hover:text-blue-700 px-3 py-2 text-sm font-medium"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Actions */}
      <section className="py-6 px-4 -mt-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <button
                onClick={getCurrentLocation}
                disabled={isGettingLocation}
                className="bg-primary-600 text-white p-4 rounded-lg hover:bg-primary-700 transition disabled:bg-gray-400"
              >
                {isGettingLocation ? (
                  <Clock className="w-6 h-6 mb-2 mx-auto animate-spin" />
                ) : (
                  <MapPin className="w-6 h-6 mb-2 mx-auto" />
                )}
                <span className="text-sm font-semibold">
                  {currentLocation ? 'Update Location' : 'Enable Location'}
                </span>
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-orange-600 text-white p-4 rounded-lg hover:bg-orange-700 transition"
              >
                <Filter className="w-6 h-6 mb-2 mx-auto" />
                <span className="text-sm font-semibold">Filters</span>
              </button>

              <a 
                href="tel:911"
                className="bg-red-600 text-white p-4 rounded-lg hover:bg-red-700 transition text-center"
              >
                <Phone className="w-6 h-6 mb-2 mx-auto" />
                <span className="text-sm font-semibold">Emergency 911</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      {showFilters && (
        <section className="py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">Filters</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type
                  </label>
                  <select
                    value={filters.serviceType}
                    onChange={(e) => setFilters({ ...filters, serviceType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {serviceTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {currentLocation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Radius: {filters.radius}km
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="100"
                      step="5"
                      value={filters.radius}
                      onChange={(e) => setFilters({ ...filters, radius: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    {currentLocation && <option value="distance">Nearest First</option>}
                    <option value="rating">Highest Rated</option>
                    <option value="price">Lowest Price</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Error Alert */}
      {error && (
        <section className="py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium whitespace-pre-line">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 font-bold text-xl">
                ×
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Location Status */}
      <section className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          {currentLocation ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <p className="text-green-800">
                Showing ambulances within {filters.radius}km of your location
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-blue-800 font-medium">
                  Location access not enabled
                </p>
                <p className="text-blue-700 text-sm mt-1">
                  Showing all available ambulances. Enable location to see nearest ambulances and get accurate ETAs.
                </p>
                <button
                  onClick={getCurrentLocation}
                  disabled={isGettingLocation}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm disabled:bg-gray-400"
                >
                  {isGettingLocation ? 'Getting Location...' : 'Enable Location Now'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          {/* Ambulances List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {currentLocation ? 'Nearby Ambulances' : 'Available Ambulances'}
                {ambulances.length > 0 && ` (${ambulances.length})`}
              </h2>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner text="Finding ambulances..." />
              </div>
            ) : ambulances.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <AmbulanceIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Ambulances Found</h3>
                <p className="text-gray-600 mb-4">
                  {currentLocation 
                    ? `No ambulances available within ${filters.radius}km`
                    : 'No ambulances available at the moment'}
                </p>
                {currentLocation && (
                  <button
                    onClick={() => setFilters({ ...filters, radius: filters.radius + 10 })}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                  >
                    Increase Search Radius
                  </button>
                )}
              </div>
            ) : (
              ambulances.map((ambulance) => (
                <div 
                  key={ambulance._id} 
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {ambulance.name}
                        </h3>
                        
                        {/* Distance and ETA */}
                        {currentLocation && ambulance.distance !== undefined && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Navigation className="w-4 h-4" />
                            <span>
                              {formatDistance(ambulance.distance)} away • 
                              ETA: {getEstimatedTime(ambulance.distance)}
                            </span>
                          </div>
                        )}

                        {/* Location Info */}
                        {ambulance.location?.address && (
                          <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{ambulance.location.address}</span>
                          </div>
                        )}

                        {/* Rating */}
                        {ambulance.rating?.average > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className="font-semibold">{ambulance.rating.average.toFixed(1)}</span>
                            <span className="text-sm text-gray-600">({ambulance.rating.totalReviews || 0})</span>
                          </div>
                        )}
                      </div>
                      
                      {ambulance.isVerified && (
                        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </div>
                      )}
                    </div>

                    {ambulance.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{ambulance.description}</p>
                    )}

                    {/* Services */}
                    {ambulance.services && ambulance.services.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Services:</p>
                        <div className="flex flex-wrap gap-2">
                          {ambulance.services.slice(0, 3).map((service, idx) => (
                            <span key={idx} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                              {service.name || service.type}
                            </span>
                          ))}
                          {ambulance.services.length > 3 && (
                            <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                              +{ambulance.services.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pricing */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b">
                      <div>
                        <p className="text-sm text-gray-600">Base Fee</p>
                        <p className="text-lg font-bold text-gray-900">
                          KES {ambulance.pricing?.baseFee?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Per Km</p>
                        <p className="text-lg font-bold text-gray-900">
                          KES {ambulance.pricing?.perKmRate?.toLocaleString() || 0}
                        </p>
                      </div>
                      {ambulance.capabilities && (
                        <div>
                          <p className="text-sm text-gray-600">Equipment</p>
                          <div className="flex gap-1 mt-1">
                            {ambulance.capabilities.basicLifeSupport && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">BLS</span>
                            )}
                            {ambulance.capabilities.advancedLifeSupport && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">ALS</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contact & Actions */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleCallDirect(ambulance.contact?.phone || ambulance.contact?.emergency)}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-center font-semibold flex items-center justify-center gap-2"
                      >
                        <Phone className="w-4 h-4" />
                        Call Now
                      </button>
                      <button
                        onClick={() => handleRequestAmbulance(ambulance)}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2"
                      >
                        <AmbulanceIcon className="w-4 h-4" />
                        Request
                      </button>
                      <button
                        onClick={() => handleViewDetails(ambulance._id)}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        title="View Details"
                      >
                        <Info className="w-5 h-5 text-gray-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Emergency Hotlines */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-800">Emergency Hotlines</h3>
              <div className="space-y-3">
                <a 
                  href="tel:911" 
                  className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition"
                >
                  <div>
                    <p className="font-semibold">General Emergency</p>
                    <p className="text-2xl font-bold text-red-600">911</p>
                  </div>
                  <Phone className="w-6 h-6 text-red-600" />
                </a>
                <a 
                  href="tel:999" 
                  className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition"
                >
                  <div>
                    <p className="font-semibold text-sm">Police</p>
                    <p className="text-xl font-bold text-orange-600">999</p>
                  </div>
                  <Phone className="w-5 h-5 text-orange-600" />
                </a>
                <a 
                  href="tel:+254797310641" 
                  className="flex items-center justify-between p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition"
                >
                  <div>
                    <p className="font-semibold text-sm">MediGo Support</p>
                    <p className="text-lg font-bold text-primary-600">+254 797 310 641</p>
                  </div>
                  <Phone className="w-5 h-5 text-primary-600" />
                </a>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Important
              </h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Stay calm and provide clear information</li>
                <li>• Keep phone line available</li>
                <li>• Have someone wait for ambulance if possible</li>
                <li>• Gather patient&apos;s medical records if available</li>
                <li>• For life-threatening emergencies, call 911 immediately</li>
              </ul>
            </div>

            {/* Statistics */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-800 mb-3">Quick Stats</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Ambulances:</span>
                  <span className="font-bold text-gray-900">{ambulances.length}</span>
                </div>
                {currentLocation && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Within {filters.radius}km:</span>
                      <span className="font-bold text-green-600">{ambulances.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nearest:</span>
                      <span className="font-bold text-blue-600">
                        {ambulances[0]?.distance 
                          ? formatDistance(ambulances[0].distance)
                          : 'N/A'}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Verified Services:</span>
                  <span className="font-bold text-green-600">
                    {ambulances.filter(a => a.isVerified).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Emergency;